import {keyBy} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {findAnswerOrFolderChildren} from '../../helpers/models/answerHelpers';
import {isTeammateNamespace} from '../../helpers/models/namespaceHelpers';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../helpers/reselectHelpers';
import {mergeAsyncStatuses} from '../../helpers/resourceTableStatusHelpers';
import {SettingsAnswersUrl} from '../../models/url/settingsUrlModel';
import {AppState} from '../../store/appState';
import {markAnswerFoldersAsStaleAction} from '../../store/data/answerFolders/answerFolderActions';
import {
  AnswerFoldersSelector,
  getAnswerFolders,
  getAnswerFoldersAsyncStatus,
  makeGetAnswerFoldersInNamespaces,
} from '../../store/data/answerFolders/answerFolderSelectors';
import {fetchAnswersAndFoldersAction, markAnswersAsStaleAction} from '../../store/data/answers/answerActions';
import {getAnswers, getAnswersAsyncStatus, getSortedAnswers} from '../../store/data/answers/answersSelectors';
import {dismissSettingsInfoBoxAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getFindShouldDisplayInfoBox} from '../../store/data/localPreferences/localPreferencesSelectors';
import {SettingsAnswersList} from './settingsAnswersList';

/*
 * Props.
 */

interface SettingsAnswersListContainerProps {
  settingsAnswersUrl: SettingsAnswersUrl;
}

/*
 * Selectors.
 */

function getSettingsAnswersUrl(_state: AppState, props: SettingsAnswersListContainerProps) {
  return props.settingsAnswersUrl;
}
function getNamespaces(_state: AppState, props: SettingsAnswersListContainerProps) {
  return [props.settingsAnswersUrl.namespace];
}

const getInfoBoxId = createSelector(getSettingsAnswersUrl, (url) =>
  isTeammateNamespace(url.namespace) ? 'individual_message_templates' : 'team_message_templates',
);

function makeGetAnswersAndFolders<P>(getAnswerFoldersInNamespaces: AnswerFoldersSelector<P>) {
  return createSelector(
    [getAnswerFoldersInNamespaces, getSortedAnswers, getNamespaces, getSettingsAnswersUrl],
    (answerFolders, answers, namespaces, answersUrl) => {
      const currentFolder = answerFolders.find(
        (f) => answersUrl.inFolder && answersUrl.folderId && f.id === answersUrl.folderId,
      );

      if (!currentFolder) {
        return [...answerFolders, ...answers.filter((a) => namespaces.includes(a.namespace))];
      }

      return findAnswerOrFolderChildren(
        currentFolder,
        keyBy(answerFolders, (a) => a.id),
        keyBy(answers, (a) => a.id),
      );
    },
  );
}

const getAsyncStatus = createSelector(
  [getAnswersAsyncStatus, getAnswerFoldersAsyncStatus],
  (answersAsyncStatus, answerFoldersAsyncStatus) =>
    mergeAsyncStatuses([answersAsyncStatus, answerFoldersAsyncStatus]),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsAnswersListContainerProps>()(() => {
  const getAnswerFoldersInNamespaces = makeGetAnswerFoldersInNamespaces(getNamespaces);
  return {
    findShouldDisplayInfoBox: getFindShouldDisplayInfoBox,
    infoBoxId: getInfoBoxId,
    answersAndFolders: makeGetAnswersAndFolders(getAnswerFoldersInNamespaces),
    loadingStatus: getAsyncStatus,
    folders: getAnswerFolders,
    answers: getAnswers,
  };
});
const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  dismissSettingsInfoBox: dismissSettingsInfoBoxAction,
  fetchAllAnswersAndFolders: fetchAnswersAndFoldersAction,
  markAnswerFoldersAsStale: markAnswerFoldersAsStaleAction,
  markAnswersAsStale: markAnswersAsStaleAction,
}));

export const SettingsAnswersListContainer = connect(mapStateToProps, mapDispatchToProps)(SettingsAnswersList);
